import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { bulkInstituteService } from '../../../services/bulkInstituteService';

const BulkInstituteUploadModal = ({ isOpen, onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [step, setStep] = useState('upload'); // upload | validate | process | complete
  const [validationResult, setValidationResult] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [uploadResults, setUploadResults] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  if (!isOpen) return null;

  const handleFileSelect = (selectedFile) => {
    if (!selectedFile) return;
    const ext = selectedFile?.name?.split('.')?.pop()?.toLowerCase();
    if (!['csv', 'xlsx', 'xls']?.includes(ext)) {
      alert('Please select a CSV or Excel (.xlsx/.xls) file');
      return;
    }
    setFile(selectedFile);
    setStep('upload');
    setValidationResult(null);
  };

  const handleInputChange = (e) => handleFileSelect(e?.target?.files?.[0]);

  const handleDrop = (e) => {
    e?.preventDefault();
    setDragOver(false);
    handleFileSelect(e?.dataTransfer?.files?.[0]);
  };

  const handleValidate = async () => {
    if (!file) return;
    setIsProcessing(true);
    try {
      const parseResult = await bulkInstituteService?.parseFile(file);
      const validation = bulkInstituteService?.validateData(parseResult?.data || []);
      setValidationResult(validation);
      setStep('validate');
    } catch (error) {
      alert(error?.message || 'Failed to parse file');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUpload = async () => {
    if (!validationResult?.valid || !validationResult?.validRows?.length) return;
    setIsProcessing(true);
    setStep('process');
    try {
      const results = await bulkInstituteService?.bulkCreateInstitutes(
        validationResult?.validRows,
        (progress) => setUploadProgress(progress)
      );
      setUploadResults(results);
      setStep('complete');
      if (onSuccess) onSuccess(results);
    } catch (error) {
      alert(error?.message || 'Bulk upload failed');
      setStep('validate');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setStep('upload');
    setValidationResult(null);
    setUploadProgress(null);
    setUploadResults(null);
    setIsProcessing(false);
    onClose();
  };

  const renderUploadStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="Building2" size={32} color="var(--color-primary)" />
        </div>
        <h3 className="text-xl font-semibold text-foreground mb-2">Bulk Institute Upload</h3>
        <p className="text-muted-foreground text-sm">Upload a CSV or Excel file to create up to 100 institute accounts at once</p>
      </div>

      {/* Drop zone */}
      <div
        onDragOver={(e) => { e?.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef?.current?.click()}
        className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-all ${
          dragOver ? 'border-primary bg-primary/5' : 'border-border hover:border-primary hover:bg-primary/5'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={handleInputChange}
          className="hidden"
        />
        <Icon name="Upload" size={40} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
        <p className="text-foreground font-medium mb-1">Click to upload or drag & drop</p>
        <p className="text-sm text-muted-foreground">CSV, XLSX, or XLS files supported</p>
      </div>

      {file && (
        <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
          <div className="flex items-center gap-3">
            <Icon name="FileSpreadsheet" size={24} color="var(--color-primary)" />
            <div>
              <p className="font-medium text-foreground text-sm">{file?.name}</p>
              <p className="text-xs text-muted-foreground">{(file?.size / 1024)?.toFixed(1)} KB</p>
            </div>
          </div>
          <button onClick={() => setFile(null)} className="text-muted-foreground hover:text-error transition-colors">
            <Icon name="X" size={18} />
          </button>
        </div>
      )}

      {/* Format info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex gap-3">
          <Icon name="Info" size={18} color="#3b82f6" className="flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-blue-900 text-sm mb-2">Required Columns:</p>
            <div className="grid grid-cols-2 gap-1 text-sm text-blue-800">
              <span>✅ <code>institute_name</code></span>
              <span>✅ <code>email</code></span>
              <span>✅ <code>city</code></span>
              <span>🟡 <code>state</code> (optional)</span>
              <span>🟡 <code>phone</code> (optional)</span>
              <span>🟡 <code>website</code> (optional)</span>
              <span className="col-span-2">🟡 <code>categories</code> (optional) — comma-separated: <em>Engineering,Management,Law</em></span>
            </div>
            <div className="mt-3 p-2 bg-blue-100 rounded border border-blue-300">
              <p className="text-xs font-semibold text-blue-900 mb-1">📋 Valid Categories (12 options):</p>
              <p className="text-xs text-blue-800 leading-relaxed">
                Engineering · Management · Law · Medical · Arts &amp; Science · Architecture · Pharmacy · Agriculture · Design · Hotel Management · Education · Nursing
              </p>
              <p className="text-xs text-blue-700 mt-1 italic">Enter multiple categories comma-separated, e.g.: <code className="bg-blue-200 px-1 rounded">Engineering,Management,Design</code></p>
            </div>
            <p className="text-xs text-blue-700 mt-2">
              💡 The downloaded template includes a guide row with all valid categories listed — delete it before uploading.
            </p>
            <p className="text-xs text-blue-700 mt-1">
              Passwords are auto-generated as: <code className="bg-blue-100 px-1 rounded">StudyExpo@[InstituteName][Year]</code>
            </p>
          </div>
        </div>
      </div>

      <div className="flex gap-3 justify-between">
        <Button
          variant="outline"
          iconName="Download"
          onClick={() => bulkInstituteService?.downloadTemplate()}
          size="sm"
        >
          Download Template
        </Button>
        <div className="flex gap-3">
          <Button variant="outline" onClick={handleClose}>Cancel</Button>
          <Button onClick={handleValidate} disabled={!file || isProcessing} loading={isProcessing} iconName="CheckCircle">
            Validate File
          </Button>
        </div>
      </div>
    </div>
  );

  const renderValidateStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
          validationResult?.valid ? 'bg-success/10' : 'bg-warning/10'
        }`}>
          <Icon
            name={validationResult?.valid ? 'CheckCircle' : 'AlertTriangle'}
            size={32}
            color={validationResult?.valid ? 'var(--color-success)' : 'var(--color-warning)'}
          />
        </div>
        <h3 className="text-xl font-semibold text-foreground mb-1">Validation Results</h3>
        <p className="text-sm text-muted-foreground">{file?.name}</p>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="bg-muted rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-foreground">{validationResult?.totalRows}</p>
          <p className="text-xs text-muted-foreground mt-1">Total Rows</p>
        </div>
        <div className="bg-success/10 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-success">{validationResult?.validCount}</p>
          <p className="text-xs text-muted-foreground mt-1">Valid</p>
        </div>
        <div className="bg-error/10 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-error">{validationResult?.errorCount}</p>
          <p className="text-xs text-muted-foreground mt-1">Errors</p>
        </div>
      </div>

      {validationResult?.errors?.length > 0 && (
        <div className="bg-error/5 border border-error/20 rounded-lg p-4 max-h-48 overflow-y-auto">
          <p className="font-medium text-error text-sm mb-2">Validation Errors:</p>
          <ul className="text-sm text-error space-y-1">
            {validationResult?.errors?.slice(0, 15)?.map((err, i) => (
              <li key={i} className="flex items-start gap-1">
                <span className="mt-0.5 flex-shrink-0">•</span>
                <span>{err}</span>
              </li>
            ))}
            {validationResult?.errors?.length > 15 && (
              <li className="font-medium">...and {validationResult?.errors?.length - 15} more errors</li>
            )}
          </ul>
        </div>
      )}

      {validationResult?.validCount > 0 && (
        <div className="bg-success/5 border border-success/20 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Icon name="CheckCircle" size={18} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-success text-sm">
                {validationResult?.validCount} institutes ready to be created
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Login credentials will be shared by the super admin directly.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Preview table */}
      {validationResult?.validRows?.length > 0 && (
        <div>
          <p className="text-sm font-medium text-foreground mb-2">Preview (first 5 rows):</p>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Institute Name</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Email</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">City</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Auto Password</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {validationResult?.validRows?.slice(0, 5)?.map((row, i) => (
                  <tr key={i} className="hover:bg-muted/50">
                    <td className="px-3 py-2 font-medium text-foreground">{row?.institute_name}</td>
                    <td className="px-3 py-2 text-muted-foreground">{row?.email}</td>
                    <td className="px-3 py-2 text-muted-foreground">{row?.city}</td>
                    <td className="px-3 py-2 font-mono text-xs text-primary">
                      {bulkInstituteService?.generatePassword(row?.institute_name)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {validationResult?.validRows?.length > 5 && (
            <p className="text-xs text-muted-foreground mt-1 text-right">
              +{validationResult?.validRows?.length - 5} more institutes
            </p>
          )}
        </div>
      )}

      <div className="flex gap-3 justify-end">
        <Button variant="outline" onClick={() => setStep('upload')}>Back</Button>
        <Button
          onClick={handleUpload}
          disabled={!validationResult?.validCount || isProcessing}
          iconName="Upload"
        >
          Create {validationResult?.validCount} Institute{validationResult?.validCount !== 1 ? 's' : ''}
        </Button>
      </div>
    </div>
  );

  const renderProcessStep = () => (
    <div className="space-y-6 py-4">
      <div className="text-center">
        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="Loader2" size={32} color="var(--color-primary)" className="animate-spin" />
        </div>
        <h3 className="text-xl font-semibold text-foreground mb-2">Creating Institute Accounts</h3>
        <p className="text-muted-foreground text-sm">Please wait while we set up each institute account...</p>
      </div>

      {uploadProgress && (
        <div className="space-y-3">
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>Processing {uploadProgress?.current} of {uploadProgress?.total}</span>
            <span>{uploadProgress?.percentage}%</span>
          </div>
          <div className="w-full bg-muted rounded-full h-3 overflow-hidden">
            <div
              className="bg-primary h-3 rounded-full transition-all duration-300"
              style={{ width: `${uploadProgress?.percentage}%` }}
            />
          </div>
          {uploadProgress?.retrying && uploadProgress?.retryInfo && (
            <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
              <span>⏳</span>
              <span>{uploadProgress?.retryInfo}</span>
            </p>
          )}
        </div>
      )}
    </div>
  );

  const renderCompleteStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="CheckCircle" size={32} color="var(--color-success)" />
        </div>
        <h3 className="text-xl font-semibold text-foreground mb-2">Upload Complete!</h3>
        <p className="text-muted-foreground text-sm">Institute accounts have been created successfully</p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-success/10 rounded-lg p-4 text-center">
          <p className="text-3xl font-bold text-success">{uploadResults?.successful?.length || 0}</p>
          <p className="text-sm text-muted-foreground mt-1">Successfully Created</p>
        </div>
        <div className="bg-error/10 rounded-lg p-4 text-center">
          <p className="text-3xl font-bold text-error">{uploadResults?.failed?.length || 0}</p>
          <p className="text-sm text-muted-foreground mt-1">Failed</p>
        </div>
      </div>

      {/* Credentials table */}
      {uploadResults?.successful?.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-foreground">Generated Credentials</p>
            <Button
              variant="outline"
              size="sm"
              iconName="Download"
              onClick={() => bulkInstituteService?.exportCredentialsToCSV(uploadResults)}
            >
              Download Credentials CSV
            </Button>
          </div>
          <div className="overflow-x-auto rounded-lg border border-border max-h-64 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Institute</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Email</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Password</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {uploadResults?.successful?.map((inst, i) => (
                  <tr key={i} className="hover:bg-muted/50">
                    <td className="px-3 py-2 font-medium text-foreground text-xs">{inst?.instituteName}</td>
                    <td className="px-3 py-2 text-muted-foreground text-xs">{inst?.email}</td>
                    <td className="px-3 py-2 font-mono text-xs text-primary">{inst?.password}</td>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-success/10 text-success">
                        <Icon name="Check" size={10} />
                        Created
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {uploadResults?.failed?.length > 0 && (
        <div className="bg-error/5 border border-error/20 rounded-lg p-4">
          <p className="font-medium text-error text-sm mb-2">Failed Entries:</p>
          <ul className="text-sm text-error space-y-1 max-h-32 overflow-y-auto">
            {uploadResults?.failed?.map((f, i) => (
              <li key={i} className="text-xs">
                <span className="font-medium">{f?.instituteName || f?.email}</span>: {f?.error}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex gap-3 justify-end">
        <Button
          variant="outline"
          iconName="Download"
          onClick={() => bulkInstituteService?.exportCredentialsToCSV(uploadResults)}
        >
          Download All Credentials
        </Button>
        <Button onClick={handleClose} iconName="Check">Done</Button>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-background rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border sticky top-0 bg-background z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Building2" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Bulk Institute Upload</h2>
              <div className="flex items-center gap-2 mt-0.5">
                {['upload', 'validate', 'process', 'complete']?.map((s, i) => (
                  <React.Fragment key={s}>
                    <div className={`w-2 h-2 rounded-full transition-colors ${
                      step === s ? 'bg-primary' :
                      ['upload', 'validate', 'process', 'complete']?.indexOf(step) > i ? 'bg-success' : 'bg-muted-foreground/30'
                    }`} />
                    {i < 3 && <div className="w-4 h-px bg-muted-foreground/20" />}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
          <button onClick={handleClose} className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-lg hover:bg-muted">
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {step === 'upload' && renderUploadStep()}
          {step === 'validate' && renderValidateStep()}
          {step === 'process' && renderProcessStep()}
          {step === 'complete' && renderCompleteStep()}
        </div>
      </div>
    </div>
  );
};

export default BulkInstituteUploadModal;
